import React, { useCallback, useEffect } from "react";
import { AdGallery, AdGalleryItem } from "@/models/AdDetailModel";
import { Box, styled, Typography } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { AD_DETAILS_2_COLUMN_MAX_WIDTH } from "@/app/layout/LayoutConstants";
import { useState } from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { DarkIconButton } from "@/app/shared/DarkIconButton";
import CloseIcon from "@mui/icons-material/Close";
import { AD_IMAGE_RATIO } from "@/services/AdImageConfig";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Link from "next/link";
import dynamic from "next/dynamic";

const GalleryVideo = dynamic(() => import("./GalleryVideo"), {
  ssr: false,
});

const OverlaidIconButton = styled(DarkIconButton)({
  position: "absolute",
});

const NavigationButton = styled(Box)(({ theme }) => ({
  top: "50%",
  transform: "translateY(-50%)",
  position: "absolute",
  cursor: "pointer",
  ".MuiSvgIcon-root": {
    color: "white",
    fontSize: 50,
  },
  filter: "drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.7))",
  [theme.breakpoints.down("sm")]: {
    ".MuiSvgIcon-root": {
      fontSize: 30,
    },
  },
}));

const OVERLAID_BUTTON_MARGIN = 2;
const OVERLAID_BUTTON_MARGIN_MOBILE = 1;

const ZoomControls = ({
  isFullScreen,
  isZoomed,
}: {
  isFullScreen: boolean;
  isZoomed: boolean;
}) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  useEffect(() => {
    if (!isFullScreen) {
      resetTransform();
    }
  }, [isFullScreen, resetTransform]);

  if (!isFullScreen) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: theme.spacing(OVERLAID_BUTTON_MARGIN),
        right: theme.spacing(OVERLAID_BUTTON_MARGIN * 4),
        zIndex: 10,
        display: "flex",
        gap: 1,
        [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
          top: theme.spacing(OVERLAID_BUTTON_MARGIN_MOBILE),
          right: theme.spacing(OVERLAID_BUTTON_MARGIN * 4 - 1),
        },
      })}
    >
      <DarkIconButton onClick={() => zoomIn()}>
        <ZoomInIcon />
      </DarkIconButton>
      <DarkIconButton onClick={() => zoomOut()} disabled={!isZoomed}>
        <ZoomOutIcon />
      </DarkIconButton>
      <DarkIconButton onClick={() => resetTransform()} disabled={!isZoomed}>
        <ZoomOutMapIcon />
      </DarkIconButton>
    </Box>
  );
};

const GallerySlideImage = ({
  src,
  isFullScreen,
}: {
  src: string;
  isFullScreen: boolean;
}) => {
  return (
    <Image
      src={src}
      fill
      alt=""
      unoptimized
      objectFit={isFullScreen ? "contain" : "cover"}
      loading="lazy"
      fetchPriority="high"
    />
  );
};

const GallerySlide = ({
  item,
  onSetFullScreen,
  isFullScreen,
  isZoomed,
  setIsZoomed,
  index,
  isInteracted,
  href,
}: {
  item: AdGalleryItem;
  onSetFullScreen: (isFullScreen: boolean) => void;
  isFullScreen: boolean;
  isZoomed: boolean;
  setIsZoomed: (isZoomed: boolean) => void;
  index: number;
  isInteracted: boolean;
  href?: string;
}) => {
  return (
    <Box
      className="embla__slide"
      onClick={() => {
        if (item.type === "image") {
          onSetFullScreen(true);
        }
      }}
      sx={{
        flex: "0 0 100%",
        minWidth: 0,
        cursor: "pointer",
        position: "relative",
        img: {
          position: "absolute",
          top: "0",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          overflow: "hidden",
        },
      }}
    >
      {item.type === "image" ? (
        item.zoomed && item.fullscreen ? (
          <TransformWrapper
            initialScale={1}
            disabled={!isFullScreen}
            onTransform={(ref) => {
              const newIsZoomed = ref.state.scale > 1.2;
              if (newIsZoomed !== isZoomed) {
                setIsZoomed(newIsZoomed);
              }
            }}
            panning={{
              disabled: !isZoomed,
            }}
          >
            <ZoomControls isFullScreen={isFullScreen} isZoomed={isZoomed} />
            <TransformComponent>
              <GallerySlideImage
                src={isFullScreen ? item.fullscreen : item.preview}
                isFullScreen={isFullScreen}
              />
              {isZoomed && (
                <GallerySlideImage
                  src={item.zoomed}
                  isFullScreen={isFullScreen}
                />
              )}
            </TransformComponent>
          </TransformWrapper>
        ) : (
          (isInteracted || index <= 1) && (
            <Link href={href || "#"}>
              <GallerySlideImage src={item.preview} isFullScreen={false} />
            </Link>
          )
        )
      ) : (
        <GalleryVideo video={item} />
      )}
    </Box>
  );
};

const MediaCount = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontWeight: "bold",
  lineHeight: 0,
  "& .MuiSvgIcon-root": {
    fontSize: "12px",
  },
}));

export function GalleryCarousel({
  gallery,
  href,
  isFullScreen = false,
  setIsFullScreen = () => {},
  isZoomed = false,
  setIsZoomed = () => {},
  activeIndex = 0,
  setActiveIndex = () => {},
}: {
  gallery: AdGallery;
  href?: string;
  isFullScreen?: boolean;
  setIsFullScreen?: (isFullScreen: boolean) => void;
  isZoomed?: boolean;
  setIsZoomed?: (isZoomed: boolean) => void;
  activeIndex?: number;
  setActiveIndex?: (index: number) => void;
}) {
  const [isInteracted, setIsInteracted] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const canFullScreen = gallery.items.some(
    (item) => item.type === "image" && item.fullscreen,
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    duration: 0,
  });

  const handleSelect = useCallback(() => {
    if (!isInteracted) {
      setIsInteracted(true);
    }
    if (emblaApi) {
      setActiveIndex(emblaApi.selectedScrollSnap());
      setCanScrollNext(emblaApi.canScrollNext());
      setCanScrollPrev(emblaApi.canScrollPrev());
    }
  }, [emblaApi, isInteracted, setActiveIndex]);

  useEffect(() => {
    emblaApi?.on("select", handleSelect);
    return () => {
      emblaApi?.off("select", handleSelect);
    };
  }, [emblaApi, handleSelect]);

  useEffect(() => {
    emblaApi?.reInit({ watchDrag: !isZoomed });
  }, [isZoomed, emblaApi]);

  useEffect(() => {
    emblaApi?.scrollTo(activeIndex);
  }, [emblaApi, activeIndex]);

  if (gallery.imageCount === 0) {
    return (
      <Box
        sx={(theme) => ({
          gridColumn: "2",
          height: theme.spacing(6),
          background: "white",
        })}
      />
    );
  }

  return (
    <Box
      className="embla"
      ref={emblaRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "100%",
        aspectRatio: 1 / AD_IMAGE_RATIO,
        ...(isFullScreen
          ? {
              borderRadius: 0,
              aspectRatio: "initial",
            }
          : {}),
      }}
    >
      <Box
        className="embla__container"
        sx={{
          display: "flex",
          height: "100%",
          ".react-transform-wrapper, .react-transform-component": {
            height: "100%",
            width: "100%",
          },
        }}
      >
        {gallery.items.map((item, index) => (
          <GallerySlide
            key={index}
            item={item}
            onSetFullScreen={setIsFullScreen}
            isFullScreen={isFullScreen}
            isZoomed={isZoomed}
            setIsZoomed={setIsZoomed}
            index={index}
            isInteracted={isInteracted}
            href={href}
          />
        ))}
      </Box>
      <NavigationButton
        className="embla__prev"
        onClick={() => emblaApi?.scrollPrev()}
        sx={{
          left: 0,
          opacity: canScrollPrev ? 1 : 0,
        }}
      >
        <NavigateBeforeIcon />
      </NavigationButton>
      <NavigationButton
        className="embla__next"
        onClick={() => emblaApi?.scrollNext()}
        sx={{
          right: 0,
          opacity: canScrollNext ? 1 : 0,
        }}
      >
        <NavigateNextIcon />
      </NavigationButton>
      {canFullScreen && (
        <OverlaidIconButton
          sx={(theme) => ({
            top: theme.spacing(OVERLAID_BUTTON_MARGIN),
            right: theme.spacing(OVERLAID_BUTTON_MARGIN),
            [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
              top: theme.spacing(OVERLAID_BUTTON_MARGIN_MOBILE),
              right: theme.spacing(OVERLAID_BUTTON_MARGIN_MOBILE),
            },
          })}
          onClick={() => setIsFullScreen(!isFullScreen)}
        >
          {isFullScreen ? <CloseIcon /> : <OpenInFullIcon />}
        </OverlaidIconButton>
      )}
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: theme.spacing(
            canFullScreen
              ? OVERLAID_BUTTON_MARGIN
              : OVERLAID_BUTTON_MARGIN_MOBILE,
          ),
          left: theme.spacing(
            canFullScreen
              ? OVERLAID_BUTTON_MARGIN
              : OVERLAID_BUTTON_MARGIN_MOBILE,
          ),
          background: "rgba(0,0,0,0.5)",
          zIndex: 1,
          color: "white",
          display: "flex",
          gap: 1,
          padding: theme.spacing(0.5, 1),
          borderRadius: 2,
          [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
            top: theme.spacing(OVERLAID_BUTTON_MARGIN_MOBILE),
            left: theme.spacing(OVERLAID_BUTTON_MARGIN_MOBILE),
          },
        })}
      >
        <MediaCount variant="caption">
          <PhotoCameraIcon />
          {gallery.imageCount}
        </MediaCount>
        {gallery.videoCount > 0 && (
          <MediaCount variant="caption">
            <PlayArrowIcon />
            {gallery.videoCount}
          </MediaCount>
        )}
      </Box>
    </Box>
  );
}
